<template>
  <portal
    v-if="open"
    to="app-modal"
  >
    <transition name="modal">
      <div
        class="modal-mask"
        :class="[theme, className]"
        @click.self="close"
      >
        <div
          class="modal-wrapper"
          :class="{ 'scroll': !hideScroll }"
        >
          <x-button
            v-if="!hideClose"
            class="modal-close-button md-icon-button"
            :disabled="loading"
            @click="close"
          >
            <icn-close />
          </x-button>

          <div
            v-if="type === 'confirm'"
            class="modal-confirm"
          >
            <div class="modal-confirm-text">
              <h1>
                {{ title }}
              </h1>
              {{ text }}
            </div>

            <div class="modal-confirm-buttons">
              <x-button
                class="md-raised"
                :disabled="loading"
                @click="close"
              >
                {{ $t('buttons.cancel') }}
              </x-button>

              <x-button
                class="md-raised md-primary"
                :disabled="loading"
                :loading="loading"
                @click="submit"
              >
                {{ $t('buttons.validate') }}
              </x-button>
            </div>
          </div>

          <div
            v-else
            :class="[
              'custom-modal-container',
              { 'modal-thin-size': widthSize === 'thin' }
            ]"
          >
            <div
              v-if="$slots.sidebar"
              class="modal-sidebar"
            >
              <slot name="sidebar" />
            </div>

            <div
              :class="['modal-container', { 'has-sidebar': $slots.sidebar }]"
            >
              <header v-if="$slots.header">
                <div
                  v-if="borderTop"
                  class="modal-border-top"
                />
                <slot name="header" />
              </header>

              <div
                :class="[{ 'no-header': !$slots.header }, {'has-body-margin': hasBodyMargin}]"
                class="modal-body"
              >
                <slot name="body" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </portal>
</template>

<script>
import icnClose from '@/svg/icn-close'

export default {
  name: 'ModalTemplate',

  components: {
    icnClose
  },

  props: {
    open: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'custom',
      validator: function(value) {
        return ['custom', 'confirm'].indexOf(value) !== -1
      }
    },
    borderTop: {
      type: Boolean,
      default: true
    },
    title: {
      type: String
    },
    text: {
      type: String
    },
    widthSize: {
      type: String,
      default: 'medium'
    },
    loading: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: 'default'
    },
    hideClose: {
      type: Boolean,
      default: false
    },
    hideScroll: {
      type: Boolean
    },
    activeExternalClickCloses: {
      type: Boolean
    },
    hasBodyMargin: {
      type: Boolean,
      default: true
    },
    className: {
      type: String,
      default: ''
    }
  },

  watch: {
    open: function(val) {
      val === true
        ? (document.body.style.overflow = 'hidden')
        : (document.body.style.overflow = 'visible')
    }
  },

  destroyed() {
    document.body.style.overflow = 'visible'
  },

  methods: {
    close() {
      if (!this.loading && (!this.hideClose || this.activeExternalClickCloses)) {
        this.$emit('close')
      }
    },

    submit() {
      this.$emit('submit')
    }
  }
}
</script>

<style lang="scss" scoped rel="stylesheet/scss">
@import '~@/styles/vars.scss';

.modal-mask {
  position: fixed;
  z-index: 10;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  /* mobile viewport bug fix */
  height: -webkit-fill-available;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.3s ease;
  overflow: scroll;

  .modal-wrapper {
    position: relative;
    max-width: 90rem;
    background-color: white;
    border-radius: 0.3rem;
    box-shadow: 0 0.2rem 0.8rem rgba(0, 0, 0, 0.33);
    transition: all 0.3s ease;
    max-height: 100%;

    &.scroll {
      overflow: scroll;
    }

    .modal-thin-size {
      max-width: 60rem;

      @media screen and (max-width: $medium-breakpoint) {
        max-width: initial;
      }
    }

    @media screen and (max-width: $small-breakpoint) {
      width: 95vw;
      max-height: 100vh;
      /* mobile viewport bug fix */
      max-height: -webkit-fill-available;
      overflow: auto;
      background-color: white;
    }
  }

  .modal-border-top {
    height: 0.4rem;
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
  }

  &.default {
    .modal-border-top {
      background-color: map_get($palette-primary, main);
    }
  }

  &.danger {
    .modal-border-top {
      background-color: $color-red;
    }
  }

  .custom-modal-container {
    display: flex;
  }

  .modal-sidebar {
    background-color: $color-diamond;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    @media screen and (max-width: $medium-breakpoint) {
      display: none;
    }
  }

  .modal-container {
    padding: calc(#{$space-small} * 2) $space-big;
    display: flex;
    flex-direction: column;

    header {
      margin-bottom: $space-big;

      &:after {
        display: block;
        position: absolute;
        left: 0;
        content: '';
        height: 0.1rem;
        width: 100%;
        opacity: 0.42;
        margin-top: 2rem;
        background-color: #d8d8d8;
      }
    }

    .modal-body {
      display: flex;
      flex-direction: column;
      height: 100%;
    }
  }

  .modal-close-button {
    position: absolute;
    top: 1.2rem;
    right: $space-medium;

    svg {
      width: 1.5rem;
      height: 1.5rem;
    }

    @media screen and (max-width: $medium-breakpoint) {
      top: $space-small;
      right: $space-small;
    }
  }

  .modal-enter,
  .modal-leave-active {
    opacity: 0;
  }

  .modal-enter .modal-wrapper,
  .modal-leave-active .modal-wrapper {
    transform: translateY(-50%);
  }

  .modal-confirm {
    .modal-confirm-text {
      text-align: center;
      color: #505065;
      font-size: 1.8rem;

      > h1 {
        font-size: 2.6rem;
        font-weight: 600;
        margin-bottom: 2.4rem;
        margin-top: 3.2rem;
      }

      @media screen and (min-width: $medium-breakpoint) {
        padding: calc(#{$space-small} * 2) 13rem;
      }
    }

    .modal-confirm-buttons {
      display: flex;
      justify-content: center;
      margin: $space-big;

      > button {
        margin: 0 $space-small;
      }
    }

    @media screen and (max-width: $medium-breakpoint) {
      padding: $space-big;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
  }

  header {
    font-family: 'Open Sans Semibold', sans-serif;
  }

  .modal-body {
    &.has-body-margin {
       margin-top: 30px;
    }
   
    &.no-header {
      margin-top: 0;
    }
  }
}
</style>
